import React, { useCallback, useMemo, useRef, useState } from "react";
import { AgGridReact } from "ag-grid-react";
import {
    CellValueChangedEvent,
    ColDef,
    themeQuartz,
} from "ag-grid-community";
import type { CustomCellRendererProps } from "ag-grid-react";
import { TrashIcon, PlusIcon } from "lucide-react";

import { Button } from "@/lib/shadcn/ui/button";
import { Input } from "@/lib/shadcn/ui/input";
import {
    useGetTagsWithCountsQuery,
    useAddTagMutation,
    useUpdateTagMutation,
    useDeleteTagMutation,
} from "@/store/booking-management-api";
import { TagWithCount } from "@/models/responses";
import {
    DeleteOptionConfirmDialog,
} from "@/lib/shadcn/components/delete-option-confirm-dialog";
import {
    BlockingApartment,
    DeleteOptionBlockedDialog,
} from "@/lib/shadcn/components/delete-option-blocked-dialog";
import { extractErrorMessage, isTagInUseError } from "@/utils/inUseError";

interface TagRow {
    id: number;
    name: string;
    activeApartmentCount: number;
}

interface ActionsRendererProps extends CustomCellRendererProps<TagRow> {
    onDelete: (row: TagRow) => void;
}

const ActionsRenderer: React.FC<ActionsRendererProps> = ({ data, onDelete }) => {
    if (!data) return null;
    return (
        <div className="text-right">
            <Button
                variant="ghost"
                size="sm"
                aria-label={`Delete tag ${data.name}`}
                data-testid={`delete-tag-button-${data.id}`}
                onClick={() => onDelete(data)}
                className="text-destructive hover:text-destructive h-8 w-8 p-0"
            >
                <TrashIcon className="h-4 w-4" aria-hidden="true" />
            </Button>
        </div>
    );
};

const TagsManagementPage: React.FC = () => {
    const { data: tags } = useGetTagsWithCountsQuery();
    const [addTag] = useAddTagMutation();
    const [updateTag] = useUpdateTagMutation();
    const [deleteTag] = useDeleteTagMutation();

    const [showAddInput, setShowAddInput] = useState(false);
    const [addInputValue, setAddInputValue] = useState("");
    const [addError, setAddError] = useState<string | undefined>(undefined);

    const [renameError, setRenameError] = useState<string | undefined>(undefined);
    /**
     * `setDataValue` (called when reverting a failed rename) re-fires
     * `onCellValueChanged` with the reverted value. Track which rows are
     * mid-revert so the second event is dropped instead of issuing a
     * spurious PUT to undo the revert itself.
     */
    const revertingRowsRef = useRef<Set<number>>(new Set());

    const [pendingDelete, setPendingDelete] = useState<TagRow | undefined>(undefined);
    const [isDeleting, setIsDeleting] = useState(false);
    const [blockedDialogState, setBlockedDialogState] = useState<
        | { tagName: string; blockingApartments: BlockingApartment[] }
        | undefined
    >(undefined);
    const [deleteError, setDeleteError] = useState<string | undefined>(undefined);

    const rows: TagRow[] = useMemo(
        () => (tags ?? []).map((t: TagWithCount) => ({ id: t.id, name: t.name, activeApartmentCount: t.activeApartmentCount })),
        [tags],
    );

    const handleDeleteClick = useCallback((row: TagRow) => {
        setPendingDelete(row);
        setDeleteError(undefined);
    }, []);

    const columnDefs: ColDef<TagRow>[] = useMemo(
        () => [
            {
                field: "name",
                headerName: "Name",
                cellDataType: "text",
                editable: true,
                cellEditor: "agTextCellEditor",
                flex: 2,
                sortable: true,
                filter: "agTextColumnFilter",
            },
            {
                field: "activeApartmentCount",
                headerName: "Active Apartments",
                cellDataType: "number",
                sortable: true,
                editable: false,
                sort: "asc",
                width: 220,
                filter: "agNumberColumnFilter",
            },
            {
                field: "actions" as keyof TagRow,
                headerName: "Actions",
                cellRenderer: ActionsRenderer,
                cellRendererParams: {
                    onDelete: handleDeleteClick,
                },
                editable: false,
                sortable: false,
                filter: false,
                width: 140,
            },
        ],
        [handleDeleteClick],
    );

    const defaultColDef = useMemo<ColDef<TagRow>>(
        () => ({
            resizable: true,
            sortable: true,
            cellStyle: {
                textAlign: "left",
                paddingLeft: "18px",
                fontSize: "13px",
            },
        }),
        [],
    );

    const onAddSubmit = useCallback(async () => {
        const trimmed = addInputValue.trim();
        if (!trimmed) {
            setAddError("Name is required");
            return;
        }
        setAddError(undefined);
        try {
            await addTag({ name: trimmed }).unwrap();
            setAddInputValue("");
            setShowAddInput(false);
        } catch (err) {
            setAddError(extractErrorMessage(err) ?? "Could not add tag");
        }
    }, [addInputValue, addTag]);

    const onCellValueChanged = useCallback(
        async (event: CellValueChangedEvent<TagRow>) => {
            if (event.colDef.field !== "name") return;
            const rowId = event.data.id;
            // Skip the synthetic event fired by `setDataValue` during a revert.
            if (revertingRowsRef.current.has(rowId)) {
                revertingRowsRef.current.delete(rowId);
                return;
            }
            const newName = String(event.newValue ?? "").trim();
            const oldName = String(event.oldValue ?? "");
            if (newName === oldName) {
                return;
            }
            if (!newName) {
                revertingRowsRef.current.add(rowId);
                event.node.setDataValue("name", oldName);
                setRenameError("Name is required");
                return;
            }
            setRenameError(undefined);
            try {
                await updateTag({ id: rowId, name: newName }).unwrap();
            } catch (err) {
                revertingRowsRef.current.add(rowId);
                event.node.setDataValue("name", oldName);
                setRenameError(extractErrorMessage(err) ?? "Could not rename tag");
            }
        },
        [updateTag],
    );

    const onConfirmDelete = useCallback(async () => {
        if (!pendingDelete) return;
        setIsDeleting(true);
        setDeleteError(undefined);
        const target = pendingDelete;
        try {
            await deleteTag({ id: target.id }).unwrap();
            setPendingDelete(undefined);
        } catch (err) {
            if (isTagInUseError(err)) {
                const entry = err.errors[0];
                setBlockedDialogState({
                    tagName: target.name,
                    blockingApartments: entry.blockingApartments.map((b) => ({
                        apartmentId: b.apartmentId,
                        apartmentName: b.apartmentName,
                    })),
                });
                setPendingDelete(undefined);
            } else {
                setDeleteError("Could not delete tag");
                setPendingDelete(undefined);
            }
        } finally {
            setIsDeleting(false);
        }
    }, [deleteTag, pendingDelete]);

    return (
        <div data-testid="tags-management-page" className="flex flex-col gap-2 p-4">
            <div className="flex items-center justify-between">
                <h1 className="text-lg font-semibold text-gray-900">Tags</h1>
                <div className="flex items-center gap-2">
                    {showAddInput ? (
                        <>
                            <Input
                                data-testid="tags-management-add-input"
                                aria-label="Tag name"
                                value={addInputValue}
                                onChange={(e) => {
                                    setAddInputValue(e.target.value);
                                    setAddError(undefined);
                                }}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter") {
                                        e.preventDefault();
                                        void onAddSubmit();
                                    }
                                }}
                                placeholder="Tag name"
                                className="w-64"
                            />
                            <Button
                                size="sm"
                                data-testid="tags-management-add-submit"
                                onClick={() => void onAddSubmit()}
                            >
                                Add
                            </Button>
                            <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => {
                                    setShowAddInput(false);
                                    setAddInputValue("");
                                    setAddError(undefined);
                                }}
                            >
                                Cancel
                            </Button>
                        </>
                    ) : (
                        <Button
                            size="sm"
                            data-testid="tags-management-add-button"
                            onClick={() => setShowAddInput(true)}
                        >
                            <PlusIcon className="mr-1.5 h-4 w-4" aria-hidden="true" />
                            Add tag
                        </Button>
                    )}
                </div>
            </div>

            {addError && (
                <div
                    data-testid="tags-management-add-error"
                    role="alert"
                    className="text-sm text-destructive"
                >
                    {addError}
                </div>
            )}
            {renameError && (
                <div
                    data-testid="tags-management-rename-error"
                    role="alert"
                    className="text-sm text-destructive"
                >
                    {renameError}
                </div>
            )}
            {deleteError && (
                <div
                    data-testid="tags-management-delete-error"
                    role="alert"
                    className="text-sm text-destructive"
                >
                    {deleteError}
                </div>
            )}

            <div className="ag-theme-quartz h-[700px] rounded-md border border-gray-200">
                <AgGridReact<TagRow>
                    theme={themeQuartz}
                    rowData={rows}
                    columnDefs={columnDefs}
                    defaultColDef={defaultColDef}
                    onCellValueChanged={onCellValueChanged}
                    getRowId={(params) => params.data.id.toString()}
                    animateRows={true}
                    stopEditingWhenCellsLoseFocus={true}
                    singleClickEdit={false}
                />
            </div>

            <DeleteOptionConfirmDialog
                optionTypeLabel="tag"
                optionName={pendingDelete?.name ?? ""}
                open={pendingDelete !== undefined}
                onOpenChange={(open) => {
                    if (!open) setPendingDelete(undefined);
                }}
                onConfirm={() => void onConfirmDelete()}
                isDeleting={isDeleting}
            />
            <DeleteOptionBlockedDialog
                optionTypeLabel="tag"
                optionName={blockedDialogState?.tagName ?? ""}
                blockingApartments={blockedDialogState?.blockingApartments ?? []}
                open={blockedDialogState !== undefined}
                onOpenChange={(open) => {
                    if (!open) setBlockedDialogState(undefined);
                }}
            />
        </div>
    );
};

export default TagsManagementPage;
